import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SplitText } from "@/components/liquid/SplitText";
import { ZoomParallax } from "@/components/liquid/ZoomParallax";
import { ParallaxY } from "@/components/liquid/ParallaxY";
import { VideoBackdrop } from "@/components/liquid/VideoBackdrop";
import { VelocityMarquee } from "@/components/liquid/VelocityMarquee";
import { pageVideo } from "@/lib/videos";
import { headingText, isHeadingBlock } from "@/lib/seo";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

export default function ArticleLiquid() {
  const { articleId } = useParams();
  const { content } = useContent();
  const { book } = useBooking();
  const article = (content?.articles || []).find((a) => a.id === articleId);

  if (!content) return <div className="min-h-screen" />;
  if (!article)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 pt-32 text-center" data-testid="article-not-found">
        <h1 className="font-display text-5xl font-normal text-[#1A1A1A]">Article not found</h1>
        <Link to="/blog" data-testid="article-back-link" className="btn-gold mt-8 px-8 py-3.5 text-sm">
          Back to the Journal
        </Link>
      </div>
    );

  const paragraphs = article.content || [];
  const [lead, ...rest] = paragraphs;
  const mid = Math.ceil(rest.length / 2);
  const firstHalf = rest.slice(0, mid);
  const secondHalf = rest.slice(mid);
  const pullQuote = rest.filter((p) => !isHeadingBlock(p))[mid - 1] || lead;

  return (
    <article className="relative z-[1] px-4 pb-24 pt-36 sm:px-8 sm:pt-44" data-testid="article-page">
      <VideoBackdrop src={pageVideo(content?.settings, "blog")} height="h-[70vh]" testid="article-video-bg" />

      <div className="mx-auto max-w-3xl">
        <Reveal>
          <Link to="/blog" data-testid="article-back-to-journal" className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-[#B38B2D] transition-colors hover:text-[#1A1A1A]">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> The Skin Journal
          </Link>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center gap-5 text-[11px] font-medium uppercase tracking-[0.25em] text-[#1A1A1A]/45">
            <span className="text-[#B38B2D]">{article.category}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{article.read_time}</span>
            {article.published_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
          </div>
        </Reveal>

        <h1 data-testid="article-title" className="mt-6 font-display text-[clamp(2.6rem,6vw,4.8rem)] font-normal leading-[1.02] tracking-tight text-[#1A1A1A]">
          <SplitText text={article.title} delay={0.25} stagger={0.014} />
        </h1>

        <Reveal delay={0.7}>
          <p className="mt-6 flex items-center gap-2 text-sm font-medium text-[#5A504B]">
            <User className="h-4 w-4 text-[#B38B2D]" aria-hidden="true" /> {article.author}
          </p>
        </Reveal>
      </div>

      <div className="mx-auto mt-12 max-w-5xl">
        <ParallaxY speed={0.35}>
          <ZoomParallax src={article.image} alt={article.title} className="h-[46vh] rounded-[4px] shadow-[0_40px_90px_rgba(26,26,26,0.16)] sm:h-[62vh]" imgClassName="" />
        </ParallaxY>
        <Reveal>
          <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#1A1A1A]/40">Fig. 01 — {article.category}</p>
        </Reveal>
      </div>

      <div className="mx-auto mt-14 max-w-3xl" data-testid="article-content">
        {lead && (
          <Reveal>
            <p className="font-display text-xl font-normal leading-relaxed text-[#1A1A1A] sm:text-2xl [&::first-letter]:float-left [&::first-letter]:mr-4 [&::first-letter]:mt-2 [&::first-letter]:font-display [&::first-letter]:text-8xl [&::first-letter]:font-normal [&::first-letter]:leading-[0.75] [&::first-letter]:text-[#D4AF37]">
              {lead}
            </p>
          </Reveal>
        )}
        {firstHalf.map((p, i) => (
          <Reveal key={i} delay={0.05}>
            {isHeadingBlock(p) ? (
              <h2 className="mt-10 font-display text-2xl font-normal text-[#1A1A1A] sm:text-3xl">{headingText(p)}</h2>
            ) : (
              <p className="mt-7 text-base font-light leading-loose text-[#3d3530]">{p}</p>
            )}
          </Reveal>
        ))}

        {pullQuote && (
          <Reveal>
            <blockquote className="my-14 border-y border-[#D4AF37]/40 py-10 text-center" data-testid="article-pull-quote">
              <p className="font-display text-2xl font-normal italic leading-snug text-[#1A1A1A] sm:text-3xl">
                “{pullQuote.length > 180 ? pullQuote.slice(0, 180).trimEnd() + "…" : pullQuote}”
              </p>
            </blockquote>
          </Reveal>
        )}

        {secondHalf.map((p, i) => (
          <Reveal key={i} delay={0.05}>
            {isHeadingBlock(p) ? (
              <h2 className="mt-10 font-display text-2xl font-normal text-[#1A1A1A] sm:text-3xl">{headingText(p)}</h2>
            ) : (
              <p className="mt-7 text-base font-light leading-loose text-[#3d3530]">{p}</p>
            )}
          </Reveal>
        ))}
      </div>

      <div className="mt-6">
        <VelocityMarquee items={["The Skin Journal", article.category || "Skin Science", "Dr. Javeria Atif"]} />
      </div>

      {(content?.settings?.section_visibility?.article_cta ?? true) && (
        <Reveal className="mx-auto mt-16 max-w-3xl">
          <div className="rounded-[4px] bg-[#1A1615] p-10 text-center sm:p-14" data-testid="article-cta">
            <p className="eyebrow text-[#D4AF37]">Personalised Advice</p>
            <h2 className="mt-4 font-display text-3xl font-normal text-white sm:text-4xl">Your skin deserves a plan, not a guess</h2>
            <p className="mx-auto mt-4 max-w-md text-sm font-light leading-relaxed text-[#E8D5CE]">
              Book a consultation with Dr. Javeria Atif at Dubai Healthcare City and get a protocol designed for your skin.
            </p>
            <button onClick={() => book()} data-testid="article-book-button" className="btn-gold mt-8 px-9 py-4 text-sm">
              Book Your Consultation
            </button>
          </div>
        </Reveal>
      )}
    </article>
  );
}
